import { Flex, GapSize } from "@/components/atoms/flex";
import { useStyle } from "./style";
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useIntl } from "react-intl";
import { Button, Divider } from "@fluentui/react-components";
import { EmailEditorProps } from "./types";
import { Dismiss16Regular } from "@fluentui/react-icons";
import { FileInput } from "@/components/atoms/fileInput";
import { LexicalEditor } from "lexical";
import { useTextMonitoring } from "@/hooks/supervisor";
import {
	SendType,
	useCurrentInteractionStore,
	CurrentInteractionState,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { EmailSubjectInput } from "./EmailSubjectInput";
import { AttachmentsView } from "./AttachmentsView";
import { insertTemplate } from "../htmlEditor/plugins/InsertTemplatePlugin";
import { InsertType } from "@/common/types/lexicalEditor";
import LexicalEditorWrapper from "../htmlEditor/LexicalEditorWrapper";
import { useEmailAttachments } from "./useEmailAttachments";
import { useDragAndDrop } from "@/hooks/dragAndDrop";
import { handleEmailPaste } from "@/common/helpers/emailHelper";

export const SingleSendEmailEditor = (props: EmailEditorProps) => {
	const intl = useIntl();
	const classes = useStyle();
	const {
		data,
		onChange,
		onSendClick,
		closeEditor,
		isFirstEmailInConversation,
	} = props;

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const [sendType, setSendType] = useState<SendType>(SendType.Send);

	useEffect(() => {
		if (sendType !== currentInteraction?.sendType) {
			setSendType(currentInteraction?.sendType ?? SendType.Send);
		}
	}, [currentInteraction]);

	const isTextMonitoring = useTextMonitoring();

	useEffect(() => {
		if (data.body) {
			insertTemplate(editorRef.current, data.body, InsertType.Replacement);
		}
	}, [data.body]);

	const currentInteractionId = useMemo(() => {
		return currentInteraction?.id ?? "";
	}, [currentInteraction]);

	const { validateAndAddModelAttachments, removeModelAttachment } =
		useEmailAttachments(data, onChange, currentInteractionId);

	const { isDragActive, dragProps } = useDragAndDrop({
		onDrop: validateAndAddModelAttachments,
		disabled: isTextMonitoring,
	});

	const toRecipientView = () => {
		return (
			<Flex
				hAlign={"space-between"}
				vAlign={"center"}
				className={classes.readOnlyRecipientContainer}
			>
				<Flex gap={GapSize.Small}>
					<span>To: </span>
					<span>{`${data.to[0]?.name!} <${data.to[0]?.address}>`}</span>
				</Flex>
				<Flex>
					<Button
						onClick={() => {
							closeEditor();
						}}
						appearance="transparent"
						icon={<Dismiss16Regular />}
					/>
				</Flex>
			</Flex>
		);
	};

	const onFilesSelected = useCallback(
		(files: FileList) => {
			const fileArray = Array.from(files);
			validateAndAddModelAttachments(fileArray);
		},
		[data, onChange]
	);

	const editorRef = useRef<LexicalEditor>();

	return (
		<Flex column className={classes.root}>
			{toRecipientView()}
			<EmailSubjectInput
				onChange={onChange}
				data={data}
				isReadOnlyMode={isTextMonitoring}
				emailHasDraft={false}
				isFirstEmailInConversation={isFirstEmailInConversation}
			/>
			<div style={{ position: "relative" }} {...dragProps}>
				<AttachmentsView
					data={data}
					isDragActive={isDragActive}
					onAttachmentRemove={removeModelAttachment}
					isRemoveAllowed={true}
				/>
				<Divider />
				<LexicalEditorWrapper
					initialHtml={data.body}
					onChange={(html: string) =>
						onChange({ ...data, body: html }, false, false, "SingleSendEmailEditor.LexicalEditorWrapper.onChange")
					}
					onPaste={handleEmailPaste}
					onPastedFiles={
						isTextMonitoring ? undefined : validateAndAddModelAttachments
					}
					additionalBar={(_editor?: LexicalEditor) => (
						<Flex
							className={classes.bottomContainer}
							gap={GapSize.ExtraSmall}
							vAlign="center"
						>
							<Button
								disabled={!data.to.length || isTextMonitoring}
								className={classes.sendButton}
								appearance="primary"
								children={intl.formatMessage({
									id: "Interaction.EmailEditor.Send",
								})}
								onClick={() => {
									onSendClick(SendType.Send);
								}}
							/>

							{!isTextMonitoring && (
								<FileInput accept="*/*" onFilesSelected={onFilesSelected} />
							)}
						</Flex>
					)}
				/>
			</div>
		</Flex>
	);
};
